import { Head, Link } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { ArrowLeft, Clock, ChevronLeft, ChevronRight, Video, Star } from 'lucide-react'
import { useState } from 'react'

interface QuizQuestion {
  question: string
  options: string[]
}

interface TrainingVideo {
  id: number
  title: string
  description: string
  video_url: string
  duration_minutes: number
  category: string
  is_required: boolean
  module_type: 'video' | 'text' | 'quiz'
  passing_score: number
  text_pages: string[]
  quiz_questions: QuizQuestion[]
  total_text_pages: number
  total_quiz_questions: number
}

interface Props extends PageProps {
  video: TrainingVideo
}

export default function TrainingShow({ video }: Props) {
  return (
    <AppLayout>
      <Head title={video.title} />

      <div className="max-w-4xl mx-auto space-y-6">
        <Header video={video} />

        {video.module_type === 'video' && (
          <VideoModule video={video} />
        )}
        {video.module_type === 'text' && (
          <TextModule video={video} />
        )}
        {video.module_type === 'quiz' && (
          <QuizModule video={video} />
        )}
      </div>
    </AppLayout>
  )
}

function Header({ video }: { video: TrainingVideo }) {
  const moduleTypeLabels = {
    video: 'Video',
    text: 'Reading',
    quiz: 'Quiz'
  }

  return (
    <div className="flex items-center gap-4">
      <Link href="/training" className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest">
        <ArrowLeft className="h-5 w-5" />
      </Link>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs px-2 py-0.5 rounded-full bg-chestnut-forest/10 text-chestnut-forest">
            {moduleTypeLabels[video.module_type]}
          </span>
          {video.is_required && (
            <span className="flex items-center gap-1 text-xs text-chestnut-gold font-medium">
              <Star className="h-3 w-3 fill-current" />
              Required
            </span>
          )}
        </div>
        <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">
          {video.title}
        </h1>
        <div className="flex items-center gap-4 mt-1">
          {video.duration_minutes > 0 && (
            <span className="text-sm text-chestnut-charcoal/60 flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {video.duration_minutes} minutes
            </span>
          )}
          {video.category && (
            <span className="text-sm text-chestnut-charcoal/60">
              {video.category}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

function VideoModule({ video }: { video: TrainingVideo }) {
  const isEmbedUrl = video.video_url?.includes('youtube') || video.video_url?.includes('vimeo')

  return (
    <>
      <div className="card p-0 overflow-hidden">
        <div className="aspect-video bg-chestnut-charcoal flex items-center justify-center">
          {video.video_url ? (
            isEmbedUrl ? (
              <iframe
                src={video.video_url}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video
                src={video.video_url}
                className="w-full h-full"
                controls
                controlsList="nodownload"
              />
            )
          ) : (
            <div className="text-center text-white">
              <Video className="h-16 w-16 mx-auto mb-4 opacity-40" />
              <p className="text-lg">Video not configured</p>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <h2 className="text-xl font-semibold text-chestnut-charcoal font-heading mb-4">
          About This Video
        </h2>
        <p className="text-chestnut-charcoal/80 whitespace-pre-wrap">
          {video.description || 'No description provided.'}
        </p>
      </div>
    </>
  )
}

function TextModule({ video }: { video: TrainingVideo }) {
  const [currentPage, setCurrentPage] = useState(0)
  const pages = video.text_pages || []
  const totalPages = pages.length

  const goToPage = (page: number) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page)
    }
  }

  return (
    <>
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-chestnut-charcoal/60">
            Page {currentPage + 1} of {totalPages}
          </span>
        </div>

        <div className="prose prose-chestnut max-w-none min-h-[300px]">
          {pages[currentPage] ? (
            <div dangerouslySetInnerHTML={{ __html: pages[currentPage] }} />
          ) : (
            <p className="text-chestnut-charcoal/60 italic">No content available for this page.</p>
          )}
        </div>

        <div className="flex items-center justify-between mt-8 pt-4 border-t border-chestnut-gray">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 0}
            className="btn-secondary flex items-center gap-2 disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </button>

          <div className="flex gap-1">
            {pages.map((_, idx) => (
              <button
                key={idx}
                onClick={() => goToPage(idx)}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentPage 
                    ? 'bg-chestnut-forest w-4' 
                    : 'bg-chestnut-gray'
                }`}
              />
            ))}
          </div>

          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages - 1}
            className="btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="card">
        <h2 className="text-xl font-semibold text-chestnut-charcoal font-heading mb-4">
          About This Module
        </h2>
        <p className="text-chestnut-charcoal/80 whitespace-pre-wrap">
          {video.description || 'Read through all pages to complete this module.'}
        </p>
      </div>
    </>
  )
}

function QuizModule({ video }: { video: TrainingVideo }) {
  const questions = video.quiz_questions || []
  const passingScore = video.passing_score || 80

  return (
    <>
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-chestnut-charcoal font-heading">
            Quiz
          </h2>
          <span className="text-sm text-chestnut-charcoal/60">
            Passing score: {passingScore}%
          </span>
        </div>

        <div className="space-y-6">
          {questions.map((q, qIdx) => (
            <div key={qIdx} className="p-4 bg-chestnut-cream rounded-lg">
              <p className="font-medium text-chestnut-charcoal mb-3">
                {qIdx + 1}. {q.question}
              </p>
              <div className="space-y-2">
                {q.options.map((option, oIdx) => (
                  <div
                    key={oIdx}
                    className="w-full text-left p-3 rounded-lg border border-chestnut-gray bg-white"
                  >
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border mr-3 text-sm">
                      {String.fromCharCode(65 + oIdx)}
                    </span>
                    {option}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h2 className="text-xl font-semibold text-chestnut-charcoal font-heading mb-4">
          About This Quiz
        </h2>
        <p className="text-chestnut-charcoal/80 whitespace-pre-wrap">
          {video.description || 'Review the questions above.'}
        </p>
      </div>
    </>
  )
}
